/**
 * Thème clair ou sombre (spécification §11), choisi par l'utilisateur ; sans choix, celui du
 * système (proposition du design system). Le choix est gardé sur l'appareil, comme « réduire la
 * transparence » : Safari ne transmet pas ce réglage du système, l'application a donc le sien
 * (data-transparence sur <html>, voir le design system).
 */
import { useCallback, useEffect, useState } from 'react';

export type ChoixTheme = 'clair' | 'sombre' | 'systeme';
export type Theme = 'clair' | 'sombre';

const CLE = 'flashcard-theme';
const CLE_TRANSPARENCE = 'flashcard-transparence';
/** Jeton `fond` de chaque thème, pour la barre de titre de la PWA (theme-color). */
const FOND: Record<Theme, string> = { clair: '#f5f5f7', sombre: '#161618' };

function lireChoix(): ChoixTheme {
  try {
    const valeur = window.localStorage.getItem(CLE);
    if (valeur === 'clair' || valeur === 'sombre' || valeur === 'systeme') return valeur;
  } catch {
    // stockage indisponible
  }
  return 'systeme';
}

function lireTransparenceReduite(): boolean {
  try {
    return window.localStorage.getItem(CLE_TRANSPARENCE) === 'reduite';
  } catch {
    return false;
  }
}

export function appliquerTransparence(reduite: boolean) {
  if (reduite) document.documentElement.dataset.transparence = 'reduite';
  else delete document.documentElement.dataset.transparence;
}

function themeSysteme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'sombre' : 'clair';
}

export function appliquerTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme === 'sombre' ? 'dark' : 'light';
  document.documentElement.style.colorScheme = theme === 'sombre' ? 'dark' : 'light';
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta === null) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.append(meta);
  }
  meta.content = FOND[theme];
}

export function useTheme() {
  const [choix, setChoixEtat] = useState<ChoixTheme>(lireChoix);
  const [systeme, setSysteme] = useState<Theme>(themeSysteme);
  const [transparenceReduite, setTransparenceEtat] = useState(lireTransparenceReduite);
  const theme: Theme = choix === 'systeme' ? systeme : choix;

  useEffect(() => {
    const requete = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!requete) return;
    const ecouter = () => setSysteme(requete.matches ? 'sombre' : 'clair');
    requete.addEventListener('change', ecouter);
    return () => requete.removeEventListener('change', ecouter);
  }, []);

  useEffect(() => appliquerTheme(theme), [theme]);
  useEffect(() => appliquerTransparence(transparenceReduite), [transparenceReduite]);

  const setChoix = useCallback((nouveau: ChoixTheme) => {
    setChoixEtat(nouveau);
    try {
      window.localStorage.setItem(CLE, nouveau);
    } catch {
      // stockage indisponible
    }
  }, []);

  const setTransparenceReduite = useCallback((reduite: boolean) => {
    setTransparenceEtat(reduite);
    try {
      window.localStorage.setItem(CLE_TRANSPARENCE, reduite ? 'reduite' : 'systeme');
    } catch {
      // stockage indisponible
    }
  }, []);

  return { choix, theme, setChoix, transparenceReduite, setTransparenceReduite };
}
